/* TODO: t() */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { Component, createRef, RefObject, ElementRef } from 'react';
import { StyleSheet, View, ScrollView } from 'react-native';

import Imm from '../../../../support/ImmutableHelpers';
import connectData, { ConnectedProps } from '../../../../support/connectData';
import formatDate from '../../../../core/support/formatDate';
import { themeColors } from '../../../uiold/Constants';

import LoadingIndicator from '../../../uiold/LoadingIndicator';
import {
  Checkbox,
  CollapsibleView,
  Text,
  DatePicker,
  Separator,
} from '../../../uiold';

import IndexedList from '../../../../support/shared/IndexedList';
import { Market } from '../../../../types/shared/Market';
import { MarketSelectorElementDefinition } from '../../../../types/shared/FormFieldDefinition';
import { MarketDates, Vendor, DateList } from '../../../../types/shared/Vendor';

// key = market.id + '|' + date;
type MarketSelections = {
  [key: string]: { market: Market; date: string };
};

type CollapsibleViewInstance = ElementRef<typeof CollapsibleView>;

type Props = {
  definition: MarketSelectorElementDefinition;
  value?: MarketDates;
  onChange: (id: string, value: MarketDates) => void;
  isVendor?: boolean;
  invoiceView?: boolean;
  vendor?: Vendor;
};

type State = {
  collapsibleRefs: ObjectOf<RefObject<CollapsibleViewInstance>>;
};

class MarketSelectorEditView extends Component<FullProps, State> {
  state: State = {
    collapsibleRefs: {},
  };

  static getDerivedStateFromProps(props: FullProps, state: State) {
    if (props.markets == null) {
      return null;
    }
    let { markets } = props;
    let collapsibleRefs: ObjectOf<RefObject<CollapsibleViewInstance>> = {};
    markets.forEach(({ id }) => {
      collapsibleRefs[id] =
        state.collapsibleRefs[id] || createRef<CollapsibleViewInstance>();
    });
    return { collapsibleRefs };
  }

  render() {
    let { isLoading, markets, isVendor, definition } = this.props;
    if (isLoading || !markets) {
      return <LoadingIndicator />;
    }

    let totalMarketDates = 0;
    let numMarkets = markets.length;
    let marketItems = markets.mapToArray((market) => {
      totalMarketDates += market.dates.length;
      if (market.hidden && isVendor === true) {
        return;
      }
      // Don't show a market if there are no dates to select.
      if (market.dates.length === 0) {
        return <span key={market.id} />;
      }
      // Collapse date selection if and only if there's more than 1 market or
      // the market dates exceed 40.
      let initiallyCollapsed = numMarkets > 1 || market.dates.length > 40;
      // Not collapsible if market dates < 4 and don't show select all dates if market dates <4
      return (
        <View style={styles.rowFlex} key={market.id}>
          {!isVendor && !definition.disableSelectAll && (
            <View style={styles.outsideCheckbox}>
              <Checkbox
                style={styles.paddingRight}
                isChecked={this._isAnyDateSelected(market)}
                onChange={() => {
                  let ref = this.state.collapsibleRefs[market.id];
                  // eslint-disable-next-line no-underscore-dangle
                  ref && ref.current && ref.current._toCollapsed();
                  this._onChangeAll(market);
                }}
              />
            </View>
          )}

          <CollapsibleView
            ref={
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              this.state.collapsibleRefs[market.id]!
            }
            key={market.id}
            isDisabled={market.dates.length <= 0}
            noLeftCheckbox={true}
            title={market.name}
            titleStyle={styles.marketText}
            style={[
              styles.box,
              isVendor || definition.disableSelectAll
                ? styles.vendorWidth
                : styles.defaultWidth,
            ]}
            subtitle={market.description}
            initialCollapseState={market.dates.length > 0 && initiallyCollapsed}
          >
            {!definition.disableSelectAll &&
              this._renderMarketDatesSelectAll(market)}
            {!definition.calendarView ? (
              <View style={styles.marketDates}>
                {this._renderMarketDates(market)}
              </View>
            ) : (
              <View style={styles.marketDates}>
                {this._renderMarketDatesAsCalendar(market)}
              </View>
            )}
            <View style={styles.marketDates}>
              {!this.props.isVendor && (
                <Text style={styles.subtitle}>
                  * Dates requested by vendor on signup or most recent change
                  request
                </Text>
              )}
            </View>
          </CollapsibleView>
        </View>
      );
    });
    if (totalMarketDates === 0) {
      return <Text type="label">No market dates available.</Text>;
    }
    return <View>{marketItems}</View>;
  }

  _renderMarketDatesSelectAll = (market: Market) => {
    let { isVendor, definition } = this.props;
    if (market.dates.length <= 3 || isVendor || definition.disableSelectAll) {
      return;
    }
    return (
      <View style={styles.selectAll}>
        <Checkbox
          isChecked={this._areAllDatesSelected(market)}
          onChange={() => this._onChangeAll(market)}
          text="(Select All Dates)"
        />
      </View>
    );
  };

  _renderMarketDates = (market: Market) => {
    let { isVendor, vendor } = this.props;
    let requestedDates = vendor?.requestedDates?.[market.id];
    return market.dates.map((date) => {
      let isDateSelected = this._isDateSelected(market, date);

      let id = market.id + date;
      return (
        <View style={styles.marketDate} key={id}>
          <Checkbox
            isChecked={isDateSelected}
            onChange={() => this._onChange(market, date)}
            text={
              this._isDateRequested(date, requestedDates)
                ? `${formatDate(date, {
                    fromUTC: true,
                    includeDayOfWeek: true,
                  })} *`
                : formatDate(date, { fromUTC: true, includeDayOfWeek: true })
            }
            isDisabled={!!isVendor && this._isDatePast(date)}
          />
        </View>
      );
    });
  };

  _renderMarketDatesAsCalendar = (market: Market) => {
    let { selections } = this.props;
    if (!selections) {
      return;
    }
    let selectionValues = this._getValue(selections);
    let availableDates = new Set(market.dates);
    let selectedDates = new Set(selectionValues?.[market.id]);
    let onDatePickerClick = (market: Market, date: string) => {
      if (!availableDates.has(date)) {
        return;
      }
      this._onChange(market, date);
    };

    return (
      <>
        <View style={styles.datePickerContainer}>
          <DatePicker
            selectedDates={selectedDates}
            availableDates={availableDates}
            onDateClick={(date) => {
              onDatePickerClick(market, date);
            }}
          />
        </View>
        <View style={styles.selectedDateList}>
          <Text style={styles.datesText}>Dates</Text>
          <Separator style={styles.datesSeparator} />
          <ScrollView style={styles.dateList}>
            {this._renderMarketDates(market)}
          </ScrollView>
        </View>
      </>
    );
  };

  _isDateSelected = (market: Market, date: string) => {
    if (!this.props.selections) {
      return false;
    }
    let key = market.id + '|' + date;
    return this.props.selections[key] != null;
  };

  _isDatePast = (date: string) => {
    let utc = new Date(date);
    let localDate = new Date(
      utc.getUTCFullYear(),
      utc.getUTCMonth(),
      utc.getUTCDate(),
    );
    let now = new Date();
    let today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // TODO: This should be based on the timezone of the market, not the viewer
    return localDate.valueOf() < today.valueOf();
  };

  _isDateRequested = (date: string, requestedDates: DateList | undefined) => {
    if (!requestedDates) {
      return false;
    }
    return requestedDates.includes(date);
  };

  _onChange = (market: Market, date: string) => {
    let { selections } = this.props;
    if (!selections) {
      return;
    }
    let key = market.id + '|' + date;
    if (this._isDateSelected(market, date)) {
      selections = Imm.deleteProperty(selections, key);
    } else {
      selections = Imm.setProperty(selections, key, { market, date });
    }
    let newValue = toValue(selections);
    this.props.onChange(this.props.definition.id, newValue);
  };

  _areAllDatesSelected = (market: Market) => {
    let { selections } = this.props;
    if (!selections) {
      return false;
    }
    for (let date of market.dates) {
      let key = market.id + '|' + date;
      if (selections[key] == null) {
        return false;
      }
    }
    return true;
  };

  _onChangeAll = (market: Market) => {
    if (this._areAllDatesSelected(market)) {
      this._selectAllDates(market, false);
    } else {
      this._selectAllDates(market, true);
    }
  };

  _selectAllDates = (market: Market, isSelect: boolean) => {
    let selections: MarketSelections = { ...this.props.selections };
    for (let date of market.dates) {
      let key = market.id + '|' + date;

      if (isSelect) {
        selections[key] = { market, date };
      } else {
        delete selections[key];
      }
    }
    let newValue = toValue(selections);
    this.props.onChange(this.props.definition.id, newValue);
  };

  _isAnyDateSelected = (market: Market): boolean => {
    let { selections } = this.props;
    let result = false;
    if (!selections) {
      return result;
    }
    for (let date of market.dates) {
      let key = market.id + '|' + date;
      if (selections[key]) {
        return true;
      }
    }
    return result;
  };

  _getValue = (selections: MarketSelections) => {
    let { markets } = this.props;
    let byMarketID: ObjectOf<Array<string>> = {};
    if (!markets) {
      return byMarketID;
    }
    markets.forEach((market) => {
      let { dates, id } = market;
      let selectedMarketDates = dates.filter(
        (date) => selections[`${id}|${date}`] != null,
      );
      // Do not include the market if no date is selected.
      // The server side validator treats object with at least one key as not empty
      // even though the value is empty.
      if (selectedMarketDates.length) {
        byMarketID[id] = selectedMarketDates;
      }
    });
    return byMarketID;
  };

  getValue = () => {
    let { selections } = this.props;
    return selections ? this._getValue(selections) : undefined;
  };
}

let styles = StyleSheet.create({
  marketText: {
    color: '#444',
    fontWeight: '600',
    maxWidth: '100%',
  },
  marketDates: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  marketDate: {
    marginVertical: 7,
    paddingRight: 7,
    minWidth: '20%',
  },
  selectAll: {
    flexDirection: 'row',
    marginVertical: 7,
  },
  box: {
    marginTop: 10,
    borderRadius: 3,
    borderWidth: 1,
    borderColor: '#ccc',
    backgroundColor: '#f4f4f4',
  },
  defaultWidth: { width: '95%' },
  vendorWidth: { width: '100%' },
  outsideCheckbox: {
    marginTop: 10,
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowFlex: { flexDirection: 'row' },
  paddingRight: { paddingRight: 2 },
  subtitle: {
    color: themeColors.mainNavText,
    fontStyle: 'italic',
    fontSize: 15,
  },
  datePickerContainer: {
    flex: 2,
    padding: 10,
    minHeight: 380,
    marginRight: 20,
    borderRadius: 5,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
  },
  selectedDateList: {
    flex: 1,
    minHeight: 380,
    paddingTop: 10,
    borderRadius: 5,
    borderColor: themeColors.borderGrey,
    borderWidth: StyleSheet.hairlineWidth,
  },
  dateList: {
    height: 0,
  },
  datesText: {
    alignSelf: 'center',
  },
  datesSeparator: {
    backgroundColor: themeColors.blue,
    marginVertical: 10,
  },
});

export function fromValue(
  value: MarketDates = {},
  markets: IndexedList<Market> | Array<Market> | undefined,
): MarketSelections {
  let selections: MarketSelections = {};
  if (!markets) {
    return selections;
  }
  // This is a bit hacky, but support Array as well as IndexedList since we'd
  // like to move away from IndexedList
  let marketsArray = Array.isArray(markets) ? markets : markets.getList();
  let marketsMap = new Map(
    marketsArray.map((market) => [market.id, market] as const),
  );
  for (let [marketID, dates] of Object.entries(value)) {
    let market = marketsMap.get(marketID);
    if (!market) {
      continue;
    }
    // TODO: Loop market.dates instead of dates here.
    for (let date of dates) {
      let id = market.id + '|' + date;
      selections[id] = { market, date };
    }
  }
  return selections;
}

function toValue(selections: MarketSelections): MarketDates {
  let result: MarketDates = {};
  for (let selection of Object.values(selections)) {
    let { market, date } = selection;
    let dates = result[market.id] || (result[market.id] = []);
    dates.push(date);
  }
  for (let dates of Object.values(result)) {
    dates.sort();
  }
  return result;
}

const connector = connectData<Props>()(
  () => ({
    markets: {
      dataStoreKey: 'markets',
      shouldFetch: (dataStore, resolvedKey) => {
        return dataStore.get(resolvedKey as any) == null;
      },
    },
  }),
  null,
  {
    computedProps: {
      selections: {
        props: ['value', 'markets'],
        compute: (
          value: MarketDates | undefined,
          markets: IndexedList<Market> | undefined,
        ) => {
          return fromValue(value, markets);
        },
      },
    },
  },
);
type FullProps = ConnectedProps<typeof connector>;
export default connector(MarketSelectorEditView);
